import React, { useState } from 'react';
import { Award, Users, Globe, Play } from 'lucide-react';
import { useSiteContent } from '../context/SiteContentContext';
import { resolveImageUrl } from '../utils/urls';
import { HEADING_PRESETS, SUBHEADING_PRESETS, resolveSizeClass } from '../utils/textSizes';
import VideoModal from './VideoModal';

const iconMap = { Award, Users, Globe };

const scrollToId = (id) => {
  const el = document.querySelector(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

// Convert legacy plain-text to <p> HTML while passing through TipTap HTML as-is.
const escapeHtml = (s) => (s || '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
const asHtml = (v) => {
  const s = (v ?? '').toString().trim();
  if (!s) return '';
  return /<\w+[^>]*>/.test(s) ? s : `<p>${escapeHtml(s)}</p>`;
};

const AboutStats = () => {
  const { stats, about } = useSiteContent();
  const [videoOpen, setVideoOpen] = useState(false);

  return (
    <>
      <section
        id="about"
        className="relative py-20 lg:py-28"
        data-testid="about-banner"
        style={{
          backgroundImage:
            `linear-gradient(135deg, rgba(10, 30, 60, 0.92) 0%, rgba(10, 61, 143, 0.85) 100%), url(${resolveImageUrl(about.banner_image) || 'https://images.unsplash.com/photo-1644325349124-d1756b79dd42'})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
          <div className="mb-10">
            <h3 className={`${resolveSizeClass(about.heading_size, HEADING_PRESETS, 'lg')} font-bold mb-2`}>
              {about.heading_id || 'Membangun merek nasional, menciptakan Jilong yang berumur seabad'}
            </h3>
            <p className={`${resolveSizeClass(about.subheading_size, SUBHEADING_PRESETS, 'xl')} font-extrabold leading-tight font-[Montserrat] whitespace-pre-line`}>
              {about.subheading || 'Mengikuti strategi baru Indonesia\ndan menargetkan tren 5G global'}
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            {(about.badges || []).map((b, i) => (
              <div key={i} className="px-5 py-2.5 bg-gradient-to-r from-teal-500/90 to-cyan-500/90 rounded-md font-semibold text-sm shadow-lg backdrop-blur-sm">
                {b}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-gray-50 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #0a3d8f 1px, transparent 0)', backgroundSize: '24px 24px' }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-extrabold text-gray-900 mb-2">{about.title || 'Tentang Kami'}</h2>
              <div className="w-16 h-1 bg-gradient-to-r from-[#0a3d8f] to-cyan-500 mb-8 rounded-full"></div>
              <div
                className="prose prose-sm sm:prose max-w-none text-gray-700 leading-relaxed text-justify [&_p]:mt-0 [&_p]:mb-4 [&_p:last-child]:mb-0 [&_a]:text-[#0a3d8f] [&_a:hover]:text-[#082e6b] [&_a]:underline"
                dangerouslySetInnerHTML={{ __html: asHtml(about.paragraph1) + asHtml(about.paragraph2) }}
              />
              <button
                onClick={() => scrollToId('#contact')}
                data-testid="about-cta-contact"
                className="mt-8 inline-flex items-center gap-2 bg-[#0a3d8f] text-white px-7 py-3 rounded-md font-semibold hover:bg-[#082e6b] transition-colors"
              >
                Pelajari Lebih Lanjut
              </button>
            </div>

            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-video">
                {about.video_thumbnail ? (
                  <img src={resolveImageUrl(about.video_thumbnail)} alt="About Jilong" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-slate-700 to-slate-900" />
                )}
                <div className="absolute inset-0 bg-gradient-to-br from-[#0a3d8f]/40 to-black/50" />
                <button
                  className="play-btn absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 rounded-full bg-white/95 flex items-center justify-center shadow-2xl"
                  aria-label="Play video"
                  data-testid="about-play-btn"
                  onClick={() => about.video_url && setVideoOpen(true)}
                >
                  <Play size={28} className="text-[#0a3d8f] ml-1" fill="#0a3d8f" />
                </button>
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="text-white text-3xl font-extrabold tracking-wider opacity-90 font-[Montserrat]">JILONG</div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-3 gap-6 mt-16">
            {stats.map((stat) => {
              const Icon = iconMap[stat.icon] || Award;
              return (
                <div key={stat.id || stat.label} className="stat-card bg-white rounded-xl p-6 shadow-md border border-gray-100" data-testid={`stat-${stat.label}`}>
                  <div className="flex items-start gap-4">
                    <div className="stat-icon-wrap shrink-0 w-14 h-14 rounded-lg bg-blue-50 text-[#0a3d8f] flex items-center justify-center">
                      <Icon size={26} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-extrabold text-[#0a3d8f] font-[Montserrat]">{stat.value}</span>
                        <span className="text-2xl font-bold text-[#e60012]">{stat.suffix}</span>
                      </div>
                      <h4 className="text-base font-bold text-gray-900 mb-2">{stat.label}</h4>
                      <p className="text-xs text-gray-600 leading-relaxed line-clamp-4">{stat.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <VideoModal open={videoOpen} onClose={() => setVideoOpen(false)} url={about.video_url} title="Jilong Video" />
    </>
  );
};

export default AboutStats;
